import {
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  UserPlus,
  RotateCcw,
  Wrench,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import {
  useDevices,
  useDeleteDevice,
  useMarkDeviceMaintenance,
  useMarkDeviceRetired,
  useRestoreDevice,
  getDeviceTypeName,
  getDeviceStatusName,
  getDeviceStatusColor,
  type DeviceType,
  type DeviceStatus,
  type DeviceFilters,
  type DeviceWithAssignee,
} from '@/hooks'

import { AssignDialog } from './assign-dialog'
import { DeviceForm } from './device-form'
import { ReturnDialog } from './return-dialog'

type ConfirmAction = {
  type: 'delete' | 'maintenance' | 'retire' | 'restore'
  device: DeviceWithAssignee
}

const deviceTypes: { value: DeviceType | 'all'; label: string }[] = [
  { value: 'all', label: '全部类型' },
  { value: 'computer', label: '电脑' },
  { value: 'monitor', label: '显示器' },
  { value: 'keyboard', label: '键盘' },
  { value: 'mouse', label: '鼠标' },
  { value: 'other', label: '其他' },
]

const deviceStatuses: { value: DeviceStatus | 'all'; label: string }[] = [
  { value: 'all', label: '全部状态' },
  { value: 'available', label: '可用' },
  { value: 'assigned', label: '已分配' },
  { value: 'maintenance', label: '维修中' },
  { value: 'retired', label: '已报废' },
]

export function DeviceList() {
  const [filters, setFilters] = useState<DeviceFilters>({
    page: 1,
    pageSize: 20,
  })
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingDevice, setEditingDevice] = useState<DeviceWithAssignee | null>(null)
  const [assigningDevice, setAssigningDevice] = useState<DeviceWithAssignee | null>(null)
  const [returningDevice, setReturningDevice] = useState<DeviceWithAssignee | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const toast = useToast()

  const { data, isLoading } = useDevices({
    ...filters,
    search: search || undefined,
  })
  const deleteMutation = useDeleteDevice()
  const maintenanceMutation = useMarkDeviceMaintenance()
  const retireMutation = useMarkDeviceRetired()
  const restoreMutation = useRestoreDevice()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters((prev) => ({ ...prev, page: 1 }))
  }

  const handleConfirmAction = async () => {
    if (!confirmAction) { return }

    const { type, device } = confirmAction
    try {
      switch (type) {
        case 'delete':
          await deleteMutation.mutateAsync(device.id)
          break
        case 'maintenance':
          await maintenanceMutation.mutateAsync({ id: device.id, data: {} })
          break
        case 'retire':
          await retireMutation.mutateAsync({ id: device.id, data: {} })
          break
        case 'restore':
          await restoreMutation.mutateAsync({ id: device.id, data: {} })
          break
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '操作失败')
    } finally {
      setConfirmAction(null)
    }
  }

  const getConfirmDialogContent = () => {
    if (!confirmAction) { return { title: '', description: '', actionText: '' } }

    const { type, device } = confirmAction
    switch (type) {
      case 'delete':
        return {
          title: '确认删除',
          description: `确定要删除设备 "${device.name}" 吗？此操作无法撤销。`,
          actionText: '删除',
          destructive: true,
        }
      case 'maintenance':
        return {
          title: '标记维修',
          description: `确定要将设备 "${device.name}" 标记为维修中吗？`,
          actionText: '确认',
          destructive: false,
        }
      case 'retire':
        return {
          title: '标记报废',
          description: `确定要将设备 "${device.name}" 标记为已报废吗？`,
          actionText: '确认',
          destructive: true,
        }
      case 'restore':
        return {
          title: '恢复可用',
          description: `确定要将设备 "${device.name}" 恢复为可用状态吗？`,
          actionText: '确认',
          destructive: false,
        }
    }
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    setEditingDevice(null)
  }

  const handleAssignSuccess = () => {
    setAssigningDevice(null)
  }

  const handleReturnSuccess = () => {
    setReturningDevice(null)
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">设备管理</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          新建设备
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索设备名称、序列号..."
              className="w-64 rounded-md border py-2 pl-10 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </form>

        <Select
          value={filters.type ?? 'all'}
          onValueChange={(value) =>
            setFilters((prev) => ({
              ...prev,
              type: value === 'all' ? undefined : (value as DeviceType),
              page: 1,
            }))
          }
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {deviceTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.status ?? 'all'}
          onValueChange={(value) =>
            setFilters((prev) => ({
              ...prev,
              status: value === 'all' ? undefined : (value as DeviceStatus),
              page: 1,
            }))
          }
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {deviceStatuses.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>设备名称</TableHead>
              <TableHead>类型</TableHead>
              <TableHead>品牌/型号</TableHead>
              <TableHead>序列号</TableHead>
              <TableHead>状态</TableHead>
              <TableHead>使用人</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  加载中...
                </TableCell>
              </TableRow>
            ) : !data?.data.length ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  暂无设备
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((device) => (
                <TableRow key={device.id}>
                  <TableCell className="font-medium">{device.name}</TableCell>
                  <TableCell>{getDeviceTypeName(device.type)}</TableCell>
                  <TableCell>
                    {device.brand || device.model
                      ? `${device.brand ?? ''} ${device.model ?? ''}`.trim()
                      : '-'}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{device.serialNumber}</TableCell>
                  <TableCell>
                    <Badge className={getDeviceStatusColor(device.status)}>
                      {getDeviceStatusName(device.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {device.assignee ? (
                      <span>
                        {device.assignee.name} ({device.assignee.employeeNo})
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="rounded p-1 hover:bg-muted">
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditingDevice(device)}>
                          <Edit className="mr-2 h-4 w-4" />
                          编辑
                        </DropdownMenuItem>
                        {device.status === 'available' && (
                          <DropdownMenuItem onClick={() => setAssigningDevice(device)}>
                            <UserPlus className="mr-2 h-4 w-4" />
                            分配
                          </DropdownMenuItem>
                        )}
                        {device.status === 'assigned' && (
                          <DropdownMenuItem onClick={() => setReturningDevice(device)}>
                            <RotateCcw className="mr-2 h-4 w-4" />
                            归还
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {device.status !== 'assigned' && device.status !== 'maintenance' && (
                          <DropdownMenuItem
                            onClick={() => setConfirmAction({ type: 'maintenance', device })}
                          >
                            <Wrench className="mr-2 h-4 w-4" />
                            标记维修
                          </DropdownMenuItem>
                        )}
                        {device.status !== 'assigned' && device.status !== 'retired' && (
                          <DropdownMenuItem
                            onClick={() => setConfirmAction({ type: 'retire', device })}
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            标记报废
                          </DropdownMenuItem>
                        )}
                        {(device.status === 'maintenance' || device.status === 'retired') && (
                          <DropdownMenuItem
                            onClick={() => setConfirmAction({ type: 'restore', device })}
                          >
                            <RotateCcw className="mr-2 h-4 w-4" />
                            恢复可用
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setConfirmAction({ type: 'delete', device })}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          删除
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data && data.meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            共 {data.meta.total} 条记录，第 {data.meta.page} / {data.meta.totalPages} 页
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
              disabled={data.meta.page <= 1}
              className="rounded-md border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              上一页
            </button>
            <button
              onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
              disabled={data.meta.page >= data.meta.totalPages}
              className="rounded-md border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {(showForm || editingDevice) && (
        <DeviceForm
          device={editingDevice ?? undefined}
          onClose={() => {
            setShowForm(false)
            setEditingDevice(null)
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {assigningDevice && (
        <AssignDialog
          device={assigningDevice}
          onClose={() => setAssigningDevice(null)}
          onSuccess={handleAssignSuccess}
        />
      )}

      {returningDevice && (
        <ReturnDialog
          device={returningDevice}
          onClose={() => setReturningDevice(null)}
          onSuccess={handleReturnSuccess}
        />
      )}

      {/* Confirm Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getConfirmDialogContent().title}</AlertDialogTitle>
            <AlertDialogDescription>{getConfirmDialogContent().description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleConfirmAction()}
              className={
                getConfirmDialogContent().destructive
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
            >
              {getConfirmDialogContent().actionText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
